import { AnalysisReportSection } from '@/components/analysis-report-section'
import { Footer } from '@/components/footer'
import { HeroSection, HomePromoVideoSection } from '@/components/hero-section'
import { OurVisionSection } from '@/components/our-vision-section'
import { useI18n } from '@/lib/i18n'
import {
  type LanguageCode,
  getPathLanguagePrefix,
  isValidLanguageCode,
  withLanguagePath,
} from '@/lib/url-language-utils'
import { useLayoutEffect } from 'react'

const HOME_ALTERNATES: ReadonlyArray<[LanguageCode, string]> = [
  ['en', 'en'],
  ['zh', 'zh-Hans'],
  ['tw', 'zh-Hant'],
  ['ja', 'ja'],
  ['ko', 'ko'],
  ['ar', 'ar'],
  ['es', 'es'],
  ['fr', 'fr'],
]

function setHeadMeta(
  attribute: 'name' | 'property',
  key: string,
  value: string,
  restorePrevious: boolean,
): () => void {
  let element = document.head.querySelector<HTMLMetaElement>(
    `meta[${attribute}="${key}"]`,
  )
  const previousContent = element?.getAttribute('content') ?? null
  const wasPresent = Boolean(element)
  if (!element) {
    element = document.createElement('meta')
    element.setAttribute(attribute, key)
    document.head.append(element)
  }
  element.content = value
  return () => {
    if (restorePrevious && wasPresent) {
      if (previousContent === null)
        element?.removeAttribute('content')
      else element?.setAttribute('content', previousContent)
    }
    else {
      element?.remove()
    }
  }
}

export function HomePage() {
  const { language, t } = useI18n()

  useLayoutEffect(() => {
    const homePathOnMount = window.location.pathname
    const fromServerHome = document.getElementById('seo-home-prelude') !== null
    const previousTitle = document.title
    const previousLang = document.documentElement.lang
    const previousDir = document.documentElement.dir
    const currentLanguage = isValidLanguageCode(language) ? language : 'en'
    const htmlLanguage = HOME_ALTERNATES.find(([code]) => code === currentLanguage)?.[1] ?? 'en'
    const canonicalUrl = `https://mentiss.ai${withLanguagePath('/', currentLanguage)}`
    const title = t('homeSeo.title')
    const socialTitle = t('homeSeo.socialTitle')
    const description = t('homeSeo.description')
    const isProductionHost = window.location.hostname === 'mentiss.ai'
      || window.location.hostname === 'www.mentiss.ai'

    document.title = title
    document.documentElement.lang = htmlLanguage
    document.documentElement.dir = currentLanguage === 'ar' ? 'rtl' : 'ltr'
    const restoreMeta = [
      setHeadMeta('name', 'google', 'notranslate', !fromServerHome),
      setHeadMeta('name', 'robots', isProductionHost ? 'index, follow' : 'noindex, nofollow', !fromServerHome),
      ...(isProductionHost
        ? [setHeadMeta('name', 'naver-site-verification', 'a2d0749c4e1e14bd43133ba429977264ae1c6a5e', !fromServerHome)]
        : []),
      setHeadMeta('name', 'description', description, !fromServerHome),
      setHeadMeta('property', 'og:type', 'website', !fromServerHome),
      setHeadMeta('property', 'og:title', socialTitle, !fromServerHome),
      setHeadMeta('property', 'og:description', description, !fromServerHome),
      setHeadMeta('property', 'og:image', 'https://mentiss.ai/asset/logo.jpg', !fromServerHome),
      setHeadMeta('property', 'og:url', canonicalUrl, !fromServerHome),
      setHeadMeta('name', 'twitter:card', 'summary_large_image', !fromServerHome),
      setHeadMeta('name', 'twitter:url', canonicalUrl, !fromServerHome),
      setHeadMeta('name', 'twitter:title', socialTitle, !fromServerHome),
      setHeadMeta('name', 'twitter:description', description, !fromServerHome),
      setHeadMeta('name', 'twitter:image', 'https://mentiss.ai/asset/logo.jpg', !fromServerHome),
    ]

    let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]')
    const previousCanonical = canonical?.getAttribute('href') ?? null
    if (!canonical) {
      canonical = document.createElement('link')
      canonical.rel = 'canonical'
      document.head.append(canonical)
    }
    canonical.href = canonicalUrl

    const previousAlternates = Array.from(document.head.querySelectorAll('link[rel="alternate"][hreflang]'))
    previousAlternates.forEach(link => link.remove())
    const homeAlternates: HTMLLinkElement[] = []
    for (const [code, hreflang] of HOME_ALTERNATES) {
      const alternate = document.createElement('link')
      alternate.rel = 'alternate'
      alternate.hreflang = hreflang
      alternate.href = `https://mentiss.ai${withLanguagePath('/', code)}`
      document.head.append(alternate)
      homeAlternates.push(alternate)
    }
    const defaultAlternate = document.createElement('link')
    defaultAlternate.rel = 'alternate'
    defaultAlternate.hreflang = 'x-default'
    defaultAlternate.href = 'https://mentiss.ai/en'
    document.head.append(defaultAlternate)
    homeAlternates.push(defaultAlternate)

    const defaultStructuredData = document.head.querySelector('#site-default-structured-data')
    const defaultStructuredDataNext = defaultStructuredData?.nextSibling ?? null
    defaultStructuredData?.remove()

    let structuredData = document.head.querySelector<HTMLScriptElement>('#home-structured-data')
    if (!structuredData) {
      structuredData = document.createElement('script')
      structuredData.id = 'home-structured-data'
      structuredData.type = 'application/ld+json'
      document.head.append(structuredData)
    }
    const pageData = {
      url: canonicalUrl,
      name: title,
      headline: t('homeSeo.heading'),
      description,
      inLanguage: htmlLanguage,
      publisher: { '@id': 'https://mentiss.ai/#organization' },
    }
    structuredData.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Organization',
          '@id': 'https://mentiss.ai/#organization',
          'name': 'Mentiss AI',
          'url': 'https://mentiss.ai',
          'logo': 'https://mentiss.ai/asset/logo.jpg',
        },
        {
          '@type': 'WebPage',
          '@id': `${canonicalUrl}#webpage`,
          ...pageData,
        },
      ],
    })

    document.getElementById('seo-home-prelude')?.remove()

    return () => {
      document.head.querySelector('#home-structured-data')?.remove()
      homeAlternates.forEach(link => link.remove())
      if (!fromServerHome)
        previousAlternates.forEach(link => document.head.append(link))
      if (!fromServerHome && previousCanonical !== null)
        canonical.href = previousCanonical
      else canonical.remove()
      restoreMeta.forEach(restore => restore())
      document.title = fromServerHome ? 'Mentiss AI' : previousTitle
      if (!fromServerHome) {
        document.documentElement.lang = previousLang
        document.documentElement.dir = previousDir
      }
      if (defaultStructuredData) {
        const next = defaultStructuredDataNext?.parentNode === document.head
          ? defaultStructuredDataNext
          : null
        document.head.insertBefore(defaultStructuredData, next)
      }

      if (window.location.pathname !== homePathOnMount) {
        const destination = new URL(window.location.href)
        if (!getPathLanguagePrefix(destination.pathname))
          destination.pathname = withLanguagePath(destination.pathname, currentLanguage)
        destination.searchParams.delete('lang')
        window.location.replace(destination.toString())
      }
    }
  }, [language, t])

  return (
    <div className="min-h-screen bg-background text-foreground">
      <main className="grid grid-cols-[minmax(0,1fr)]">
        <section id="hero" className="lg:col-start-1 lg:row-start-1">
          <HeroSection />
        </section>

        <HomePromoVideoSection />

        <section id="benchmark">
          <AnalysisReportSection />
        </section>

        <section id="vision">
          <OurVisionSection />
        </section>

      </main>

      <Footer />
    </div>
  )
}
